import React, {useState, useEffect, useCallback, useRef} from 'react';
import {Button, Icon, Block, Row} from '../../components/Component';
import Swal from 'sweetalert2';
import FileProvider from '../../plasticos/providers/file.provider';
import {getFormattedSize} from '../../plasticos/helpers/files.helper';
import BorrarDocs from '../../plasticos/helpers/borrardocs';
import DeleteDocument from '../../plasticos/helpers/deleteDocuments';
import {updateTemporalesMroDocuments, getDocumentsTypeMro, insertTypeFileMro, updateTypeFileMro} from '../temporales';

const MroDocumentsList = ({
  poNumber,
  currentTab = 'Import',
  onDeleteSuccess,
  onCountsChange,
  refreshTrigger = 0
}) => {
  const [allFiles, setAllFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [assigningId, setAssigningId] = useState(null);

  const onCountsChangeRef = useRef(onCountsChange);
  useEffect(() => {
    onCountsChangeRef.current = onCountsChange;
  }, [onCountsChange]);

  const removeClonFiles = (files) => {
    if (!files || !Array.isArray(files)) return [];
    return files.filter(item => !item.name?.startsWith('orders/'));
  };

  const getFiles = useCallback(async () => {
    if (!poNumber) return;
    
    setSubmitting(true);
    try {
      // 1. Obtener archivos desde S3
      const s3Response = await new FileProvider().getFiles(poNumber);
      const s3Files = removeClonFiles(s3Response?.data?.files || []);

      // 2. Obtener clasificación de tipos desde MySQL
      const dbFiles = await getDocumentsTypeMro(poNumber);

      // 3. Cruzar información de S3 con la clasificación de BD
      const classifiedFiles = s3Files.map(sFile => {
        const cleanName = sFile.name || '';
        const ext = sFile.extension || '';
        const normalizedName = cleanName.replace(/  +/g, ' ').replaceAll(' ', '_');

        // Buscar coincidencia en dbFiles
        const match = dbFiles.find(dbF => {
          const dbFullName = (dbF.file_name || '').trim().toLowerCase();
          const dbBaseName = dbFullName.replace(/\.[^/.]+$/, '');
          
          const s3BaseName = (sFile.name || '').trim().toLowerCase();
          const s3Ext = (sFile.extension || '').trim().toLowerCase();
          const s3FullName = `${s3BaseName}${s3Ext}`;

          return (
            dbFullName === s3FullName ||
            dbBaseName === s3BaseName ||
            dbFullName.replace(/[\s_-]+/g, '') === s3FullName.replace(/[\s_-]+/g, '') ||
            dbBaseName.replace(/[\s_-]+/g, '') === s3BaseName.replace(/[\s_-]+/g, '')
          );
        });

        return {
          ...sFile,
          id_file: match ? match.id_file : null,
          doc_type: match && match.doc_type ? match.doc_type : 'Sin Asignar',
          type_id: match ? match.type_id : null
        };
      });

      setAllFiles(classifiedFiles);

      // 4. Notificar conteos al componente padre sin causar ciclo de dependencias
      if (onCountsChangeRef.current) {
        const importCount = classifiedFiles.filter(f => f.doc_type === 'Import').length;
        const exportCount = classifiedFiles.filter(f => f.doc_type === 'Export').length;
        const unassignedCount = classifiedFiles.filter(f => f.doc_type === 'Sin Asignar' || !f.doc_type).length;
        onCountsChangeRef.current({
          total: classifiedFiles.length,
          importCount,
          exportCount,
          unassignedCount
        });
      }

    } catch (error) {
      console.error('Error al obtener documentos y tipos:', error);
      setAllFiles([]);
    } finally {
      setSubmitting(false);
    }
  }, [poNumber]);

  useEffect(() => {
    getFiles();
  }, [getFiles, refreshTrigger]);

  // Manejar asignación o reasignación de tipo
  const handleAssignType = async (file, newType) => {
    if (!newType) return;
    setAssigningId(file.name);

    try {
      const fullName = `${file.name || ''}${file.extension || ''}`;
      const res = await insertTypeFileMro({
        id_file: file.id_file || null,
        type: newType,
        po_number: poNumber,
        file_name: fullName,
        extension: file.extension || ''
      });

      if (res && res.status === 'success') {
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: `Documento asignado a ${newType}`,
          showConfirmButton: false,
          timer: 2000
        });
        await getFiles();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error al asignar tipo',
          text: res?.message || 'Ocurrió un error inesperado',
          confirmButtonColor: '#18263F'
        });
      }
    } catch (err) {
      console.error('Error asignando tipo:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error inesperado',
        text: err.message || 'No se pudo conectar con el servidor',
        confirmButtonColor: '#18263F'
      });
    } finally {
      setAssigningId(null);
    }
  };

  const borrar = (fileName, fileIdentifier) => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: "¡No podrás revertir esta acción!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#18263F',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        let obj = {
          file_name: poNumber,
          file_indentifier: fileIdentifier,
        };
        
        try {
          await DeleteDocument(obj);
          await BorrarDocs(poNumber, fileName);
          
          await getFiles();
          
          // Contar archivos restantes
          const response = await new FileProvider().getFiles(poNumber);
          const filteredFiles = removeClonFiles(response?.data?.files || []);
          const remainingFilesCount = filteredFiles.length;
          
          await updateTemporalesMroDocuments(poNumber, remainingFilesCount);
          
          Swal.fire({
            title: '¡Eliminado!',
            text: 'El archivo ha sido eliminado.',
            icon: 'success',
            confirmButtonColor: '#18263F'
          });
          
          if (onDeleteSuccess) {
            onDeleteSuccess();
          }
        } catch (error) {
          console.error('Error al borrar documento:', error);
          Swal.fire({
            title: 'Error',
            text: 'No se pudo eliminar el documento',
            icon: 'error',
            confirmButtonColor: '#18263F'
          });
        }
      }
    });
  };

  // Filtrar archivos según la pestaña actual
  const currentFiles = allFiles.filter(file => {
    if (currentTab === 'Sin Asignar') {
      return file.doc_type === 'Sin Asignar' || !file.doc_type;
    }
    return file.doc_type === currentTab;
  });

  if (submitting) {
    return (
      <div style={{textAlign: 'center', padding: '20px'}}>
        <span className="spinner-border spinner-border-sm" />
        <span style={{marginLeft: '10px'}}>Cargando documentos...</span>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div style={{marginTop: '10px'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
          <Button
            color="secondary"
            size="sm"
            onClick={getFiles}
          >
            {submitting ? 'Buscando...' : 'Actualizar lista'}
          </Button>
          <span style={{fontSize: '13px', color: '#6c757d'}}>
            {currentFiles.length} documento{currentFiles.length !== 1 ? 's' : ''} en {currentTab}
          </span>
        </div>
        
        {currentFiles.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '30px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px dashed #dee2e6'
          }}>
            <Icon name="file-text" style={{fontSize: '32px', color: '#adb5bd', marginBottom: '8px'}} />
            <p className="text-muted mb-0">
              {currentTab === 'Sin Asignar' 
                ? '¡Excelente! No hay documentos pendientes de clasificar.'
                : `No hay documentos de ${currentTab} registrados.`}
            </p>
          </div>
        ) : (
          <Block className="border" style={{borderRadius: '8px', overflow: 'hidden'}}>
            <table className="table table-hover" style={{marginBottom: 0}}>
              <thead style={{backgroundColor: '#f5f6fa'}}>
                <tr>
                  <th className="text-dark" style={{fontSize: '13px', padding: '10px 12px'}}>Nombre del Archivo</th>
                  <th className="text-dark" style={{fontSize: '13px', padding: '10px 12px', textAlign: 'center'}}>Tamaño</th>
                  {currentTab === 'Sin Asignar' ? (
                    <th className="text-dark" style={{fontSize: '13px', padding: '10px 12px', textAlign: 'center', width: '200px'}}>Asignar a</th>
                  ) : (
                    <th className="text-dark" style={{fontSize: '13px', padding: '10px 12px', textAlign: 'center', width: '160px'}}>Reasignar</th>
                  )}
                  <th className="text-dark" style={{fontSize: '13px', padding: '10px 12px', textAlign: 'center', width: '110px'}}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentFiles.map((file, index) => {
                  if (file?.extension?.includes('undefined')) return null;
                  const isAssigning = assigningId === file.name;
                  return (
                    <tr key={index} style={{verticalAlign: 'middle'}}>
                      <td style={{padding: '10px 12px', fontSize: '13px'}}>
                        <Icon name="file" style={{marginRight: '8px', color: '#6576ff'}} />
                        <strong>{file.name}</strong>{file.extension}
                      </td>
                      <td style={{textAlign: 'center', padding: '10px 12px', fontSize: '12px', color: '#6c757d'}}>
                        {getFormattedSize(file.size, 2)}
                      </td>
                      
                      {/* Selector de Asignación / Reasignación */}
                      <td style={{textAlign: 'center', padding: '8px 12px'}}>
                        {currentTab === 'Sin Asignar' ? (
                          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'}}>
                            {isAssigning ? (
                              <span className="spinner-border spinner-border-sm text-primary" />
                            ) : (
                              <select
                                className="form-control form-control-sm"
                                style={{
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  borderColor: '#f7971e',
                                  backgroundColor: '#fffcf5'
                                }}
                                defaultValue=""
                                onChange={(e) => handleAssignType(file, e.target.value)}
                              >
                                <option value="" disabled>Seleccionar...</option>
                                <option value="Import">Import</option>
                                <option value="Export">Export</option>
                              </select>
                            )}
                          </div>
                        ) : (
                          <select
                            className="form-control form-control-sm"
                            style={{
                              borderRadius: '6px',
                              fontSize: '12px'
                            }}
                            value={file.doc_type || currentTab}
                            disabled={isAssigning}
                            onChange={(e) => handleAssignType(file, e.target.value)}
                          >
                            <option value="Import">Import</option>
                            <option value="Export">Export</option>
                          </select>
                        )}
                      </td>

                      {/* Botones de Descargar y Borrar */}
                      <td style={{textAlign: 'center', padding: '8px 12px'}}>
                        <div style={{display: 'flex', justifyContent: 'center', gap: '10px', alignItems: 'center'}}>
                          <a
                            href={file.file}
                            target="_blank"
                            className="link-primary"
                            rel="noreferrer"
                            title="Descargar archivo"
                          >
                            <Icon
                              className="text-ssbazul"
                              name="file-download"
                              style={{fontSize: '18px'}}
                            />
                          </a>
                          <a
                            href="#delete"
                            title="Eliminar archivo"
                            onClick={(event) => {
                              event.preventDefault();
                              if (event.isTrusted) {
                                borrar(
                                  file.name + file.extension,
                                  file.name
                                );
                              }
                            }}
                          >
                            <Icon
                              className="text-danger"
                              name="trash"
                              style={{fontSize: '18px'}}
                            />
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Block>
        )}
      </div>
    </React.Fragment>
  );
};

export default MroDocumentsList;

